import * as XLSX from 'xlsx';
import type { TranslationKey } from '../types/i18n';

const REQUIRED_COLUMNS = ['First Name', 'Last Name'];
const OPTIONAL_COLUMNS = [
  'Phone Number',
  'Extension', // call routing to a specific employee or department
  'Backup Phone Number',
  'Department Name',
  'Department Phone Number',
  'Department Email',
  'Job Title',
  'Email',
  'Backup Email',
  'Company',
  'Location',
  'Department Description',
];

export const EMPLOYEE_DIRECTORY_COLUMNS = {
  REQUIRED: REQUIRED_COLUMNS,
  OPTIONAL: OPTIONAL_COLUMNS,
  ALL: [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS],
};

// Staged files are persisted base64-encoded inside the shared
// elora_agent_form_state sessionStorage blob (AgentForm's
// saveCapabilitiesData). sessionStorage caps out around 5 MB and base64
// inflates by ~33%, so cap raw files at 3 MB to keep the whole blob —
// including every other persisted form field — safely under quota.
export const MAX_STAGED_FILE_BYTES = 3 * 1024 * 1024;

export type EmployeeDirectoryFileCheck =
  | {
      ok: true;
      // The selected file, or a rewritten copy with the missing columns added
      file: File;
      rows: string[][];
      // Lowercased, trimmed header -> column index in `rows`
      columnIndexMap: Record<string, number>;
    }
  | { ok: false; error: TranslationKey };

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
  });

const readArrayBuffer = (file: File): Promise<ArrayBuffer | null> =>
  new Promise(resolve => {
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = e => resolve(e.target?.result as ArrayBuffer);
    reader.readAsArrayBuffer(file);
  });

/**
 * The checks an employee directory file must pass before it's previewed,
 * staged or uploaded. Column and row validation itself (required fields,
 * string length, row cap, row content, ...) is not duplicated here — it's
 * delegated to the backend's validate_employee_directory, the single source
 * of truth, once an agent id exists.
 *
 * `staged`: the file will be held in form state until the agent is created,
 * so it's also capped at MAX_STAGED_FILE_BYTES.
 */
export const checkEmployeeDirectoryFile = async (
  selected: File,
  { staged }: { staged: boolean }
): Promise<EmployeeDirectoryFileCheck> => {
  if (!selected.name.toLowerCase().endsWith('.xlsx')) {
    return { ok: false, error: 'Only .xlsx files are accepted.' };
  }

  if (staged && selected.size > MAX_STAGED_FILE_BYTES) {
    return {
      ok: false,
      error: 'The file is too large. Files up to 3 MB are supported.',
    };
  }

  const buffer = await readArrayBuffer(selected);
  if (!buffer) {
    return {
      ok: false,
      error: 'The file could not be read. Please try again.',
    };
  }

  let wb: XLSX.WorkBook;
  let rows: string[][];
  try {
    wb = XLSX.read(new Uint8Array(buffer), { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
  } catch {
    return {
      ok: false,
      error:
        'The file could not be read. Please make sure it is a valid .xlsx file.',
    };
  }
  if (rows.length < 2) {
    return { ok: false, error: 'The file appears to be empty.' };
  }

  // Normalize headers to lowercase+trimmed to match backend's case-insensitive
  // column matching (backend does col.strip().lower() before lookup).
  const fileHeaders = rows[0] as string[];
  const columnIndexMap = Object.fromEntries(
    fileHeaders.map((h, i) => [String(h).trim().toLowerCase(), i])
  );

  // Inject columns the backend requires but the file omits.
  // The backend rejects uploads missing any expected column header; users
  // may legitimately leave optional columns (e.g. Backup Phone Number) out
  // of their file entirely if they have no data for them.
  const missingCols = EMPLOYEE_DIRECTORY_COLUMNS.ALL.filter(
    col => columnIndexMap[col.toLowerCase()] === undefined
  );
  let file = selected;
  if (missingCols.length > 0) {
    const patchedRows = [
      [...(rows[0] as string[]), ...missingCols],
      ...(rows.slice(1) as string[][]),
    ];
    const patchedWs = XLSX.utils.aoa_to_sheet(patchedRows);
    wb.Sheets[wb.SheetNames[0]] = patchedWs;
    const bytes: Uint8Array = XLSX.write(wb, {
      type: 'array',
      bookType: 'xlsx',
    });
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    file = new File([blob], selected.name, { type: blob.type });
  }

  return { ok: true, file, rows, columnIndexMap };
};
